import { apiConfig } from '@/shared/config/api.config';

/**
 * Token from ABP response headers (readable by JS). HttpOnly antiforgery cookies
 * are not visible in document.cookie — pairing cookie + header requires this.
 *
 * **Cross-origin SPA (different host than API):** `document.cookie` on the SPA origin cannot read
 * `XSRF-TOKEN` set for the API host. Mutating requests then depend on header tokens from prior GETs.
 * CORS must expose those headers (e.g. `Abp-RequestVerificationToken`, `RequestVerificationToken`,
 * `X-XSRF-TOKEN`); ABP’s `WithAbpExposedHeaders()` normally does this — if only `_AbpErrorFormat`
 * appears in `Access-Control-Expose-Headers`, POSTs may fail antiforgery and redirect to `/Error`.
 */
let abpVerificationTokenFromResponse: string | undefined;

function parseDocumentCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  const out: Record<string, string> = {};
  for (const part of document.cookie.split(';')) {
    const idx = part.indexOf('=');
    if (idx === -1) continue;
    const k = part.slice(0, idx).trim();
    try {
      out[k] = decodeURIComponent(part.slice(idx + 1).trim());
    } catch {
      out[k] = part.slice(idx + 1).trim();
    }
  }
  return out;
}

/**
 * Reads the XSRF-TOKEN cookie (ASP.NET Core often emits this name for JS-readable pairing
 * with the HttpOnly antiforgery cookie). Match is case-insensitive on the cookie name.
 */
export function getXsrfTokenFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const cookies = parseDocumentCookies();
  if (cookies['XSRF-TOKEN']) return cookies['XSRF-TOKEN'];
  if (cookies['xsrf-token']) return cookies['xsrf-token'];
  for (const key of Object.keys(cookies)) {
    if (key.toLowerCase() === 'xsrf-token') return cookies[key];
  }
  return undefined;
}

/** Non-HttpOnly cookies / meta (fallback when response header not exposed under CORS). */
export function getAntiForgeryTokenFromCookieOrMeta(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const xsrf = getXsrfTokenFromCookie();
  if (xsrf) return xsrf;
  const meta = document.querySelector('meta[name="RequestVerificationToken"]');
  const fromMeta = meta?.getAttribute('content')?.trim();
  if (fromMeta) return fromMeta;
  const cookies = parseDocumentCookies();
  const preferred = [
    'RequestVerificationToken',
    'Abp.AntiforgeryToken',
    '__RequestVerificationToken',
  ];
  for (const key of preferred) {
    if (cookies[key]) return cookies[key];
  }
  for (const key of Object.keys(cookies)) {
    if (key.startsWith('.AspNetCore.Antiforgery')) return cookies[key];
  }
  return undefined;
}

export function rememberTokenFromResponseHeaders(headers: Headers): void {
  const names = [
    'Abp-RequestVerificationToken',
    'RequestVerificationToken',
    'X-XSRF-TOKEN',
    'X-Xsrf-Token',
  ];
  for (const name of names) {
    const v = headers.get(name);
    if (v?.trim()) {
      abpVerificationTokenFromResponse = v.trim();
      return;
    }
  }
}

/** Headers ABP expects on POST/PUT/PATCH/DELETE (cookie auth + antiforgery). */
export function getAntiForgeryHeadersForMutatingRequest(): Record<string, string> {
  /**
   * Prefer cookie/meta token over the in-memory header token from a previous response.
   * Otherwise a stale `abpVerificationTokenFromResponse` can disagree with `XSRF-TOKEN`
   * (double-submit cookie), causing 400 on mutating requests while the JSON body is valid.
   */
  const fromCookie = getAntiForgeryTokenFromCookieOrMeta();
  const token = fromCookie || abpVerificationTokenFromResponse;
  if (fromCookie) {
    abpVerificationTokenFromResponse = fromCookie;
  }
  const h: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (token) {
    h['RequestVerificationToken'] = token;
    h['X-XSRF-TOKEN'] = token;
    h['Abp-RequestVerificationToken'] = token;
  }
  return h;
}

/**
 * GET /api/abp/application-configuration — sets antiforgery cookie on the API host
 * and often returns Abp-RequestVerificationToken. Call before login POST when the
 * browser has no prior cookie for that origin.
 */
function syncInMemoryTokenFromCookie(): void {
  const fromCookie = getAntiForgeryTokenFromCookieOrMeta();
  if (fromCookie && !abpVerificationTokenFromResponse) {
    abpVerificationTokenFromResponse = fromCookie;
  }
}

async function primeGet(url: string): Promise<void> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
  rememberTokenFromResponseHeaders(res.headers);
  syncInMemoryTokenFromCookie();
}

/**
 * GET /api/abp/application-configuration (and optional site root) with credentials so the
 * API can set .AspNetCore.Antiforgery + XSRF-TOKEN. Cross-origin SPAs cannot read API
 * cookies in document.cookie; pairing then relies on exposed response headers or backend CORS.
 */
export async function primeAbpAntiforgerySession(): Promise<void> {
  const base = apiConfig.baseUrl.replace(/\/$/, '');
  const configUrl = `${base}/abp/application-configuration`;
  try {
    await primeGet(configUrl);
    if (!abpVerificationTokenFromResponse && !getXsrfTokenFromCookie()) {
      try {
        const apiOrigin = base.startsWith('http')
          ? new URL(base).origin
          : typeof window !== 'undefined'
            ? window.location.origin
            : '';
        if (apiOrigin) await primeGet(`${apiOrigin}/`);
      } catch {
        // ignore optional root prime
      }
    }
  } catch {
    // network / CORS — login may still fail; avoid throwing here
  }
}
